import CarouselCard from 'components/OfferList/OfferCards/CarouselCard'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import React, { useMemo } from 'react'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import LEOfferPriceDetails from './LEOfferPriceDetails'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import { themeClassName } from 'lib/theme/themeUtils'
import { OptimizelyServerSideExperiments, activateHeroFinderAbTest, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import { isLuxuryPlusEnabled } from 'loyalty/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import useIsHomepage from 'home/hooks/isHomepage'

interface Props {
  offer: App.Offer | App.OfferSummary
  onClick: () => void
  className?: string;
  offerUrl: string;
}

function LEOfferCard(props: Props) {
  const { offer, onClick, className, offerUrl } = props
  const { lowestPricePackage } = offer
  const isLuxPlusEnabled = useAppSelector(state => isLuxuryPlusEnabled(state))
  activateHeroFinderAbTest(offer.id)
  const isHomePage = useIsHomepage()
  const showMoreOfferOnHeroCarousel = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.showMoreOffersOnHeroCarousel, isHomePage)

  const reviews = useMemo(() => ({
    showReviews: showUserReviewsRating(offer.property?.rating, offer.property?.reviewsTotal),
    rating: offer.property?.rating,
    reviewsTotal: offer.property?.reviewsTotal,
    ratingSubtitle: offer.property?.reviewsSource === 'google' ? 'From online reviews' : undefined,
  }), [offer.property])

  const paletteValue: ProductPalette = isLuxPlusEnabled && !!offer.loyalty?.access ? 'loyalty' : isUltraLuxOffer(offer) ? 'ultralux' : 'default'

  const memberPrice = lowestPricePackage?.memberPrice ?? 0
  const showMemberPrice = isLuxPlusEnabled && memberPrice > 0
  const saveAmount = showMemberPrice ? (lowestPricePackage?.price ?? 0) - memberPrice : undefined
  const { loyaltyInclusions } = useOfferInclusions(lowestPricePackage)

  return (
    <ProductPaletteProvider palette={paletteValue}>
      <CarouselCard
        data-testid="offer-card"
        className={className}
        bookmarkButton={<BookmarkButton offer={offer} />}
        description={offer.name}
        image={offer.image}
        location={[offer.locationHeading, offer.locationSubheading].filter((t) => t).join(', ')}
        title={offer.property?.name ?? ''}
        offerPriceDetails={<LEOfferPriceDetails offer={offer} />}
        offerUrl={offerUrl}
        productType={offer.productType}
        reviews={reviews}
        urgencyLabels={<LabelGroup className={showMoreOfferOnHeroCarousel ? themeClassName('default') : undefined}>
          <OfferLabels offer={offer} saveAmount={saveAmount} hasMemberInclusions={loyaltyInclusions.length > 0} />
          {offer.badge && <OfferBadgeTag badge={offer.badge} />}
        </LabelGroup>}
        onClick={onClick}
      />
    </ProductPaletteProvider>
  )
}

export default LEOfferCard
